import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import type { PatrulhasDesempenho } from "../../../../types/desempenho";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface PatrulhasTabProps {
  data: PatrulhasDesempenho | null;
}

const formatDateBR = (dateStr: string) => {
  if (!dateStr) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{Number(entry.value).toLocaleString('pt-BR')}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function PatrulhasTab({ data }: PatrulhasTabProps) {
  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum dado disponível. Use os filtros acima para buscar informações.
        </CardContent>
      </Card>
    );
  }

  const formatMinutos = (minutos: number) => {
    const horas = Math.floor(minutos / 60);
    const mins = Math.round(minutos % 60);
    return `${horas}h ${mins}m`;
  };

  // Preparar dados de evolução diária
  const evolucaoDiariaData = data.volumetria?.porDia?.map(dia => ({
    data: formatDateBR(dia.referencia),
    "Total": dia.totalPatrulhas,
    "Concluídas": dia.statusDistribuicao.concluidas,
    "Incompletas": dia.statusDistribuicao.incompletas,
    "Canceladas": dia.statusDistribuicao.canceladas
  })) || [];

  // Preparar dados de distribuição por hora
  const distribuicaoHorariaData = data.volumetria?.porHora?.map(hora => ({
    hora: `${hora.referencia}h`,
    "Patrulhas": hora.totalPatrulhas
  })) || [];

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Patrulhas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.estatisticas.totalPatrulhas}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Duração Média</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatMinutos(data.estatisticas.duracaoMedia)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Concluídas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.estatisticas.statusDistribuicao.concluidas}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Incompletas/Canceladas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {data.estatisticas.statusDistribuicao.incompletas +
               data.estatisticas.statusDistribuicao.canceladas}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos de Evolução */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Evolução Diária</CardTitle>
            <CardDescription>Patrulhas por status ao longo do tempo</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolucaoDiariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Total" stroke="oklch(0.5 0.2 240)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Concluídas" stroke="oklch(0.45 0.15 142)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Incompletas" stroke="oklch(0.5 0.2 60)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Canceladas" stroke="oklch(0.5 0.2 20)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Horário</CardTitle>
            <CardDescription>Volume de patrulhas por hora</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={distribuicaoHorariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="hora" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Patrulhas" fill="oklch(0.45 0.15 142)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Responsáveis por Função */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Supervisores */}
        <Card>
          <CardHeader>
            <CardTitle>Supervisores</CardTitle>
            <CardDescription>{data.funcoes.supervisor.length} responsáveis</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-sm">
                  <th className="text-left p-2">Nick</th>
                  <th className="text-center p-2">Tempo</th>
                </tr>
              </thead>
              <tbody>
                {[...data.funcoes.supervisor]
                  .sort((a, b) => b.tempoTotal - a.tempoTotal)
                  .map((resp) => (
                  <tr key={resp.militar.id} className="border-b hover:bg-secondary/30">
                    <td className="p-2">
                      <div className="font-medium text-sm">{resp.militar.nome}</div>
                      <div className="text-xs text-muted-foreground">{resp.militar.patente}</div>
                    </td>
                    <td className="p-2 text-center text-sm">{formatMinutos(resp.tempoTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Monitores */}
        <Card>
          <CardHeader>
            <CardTitle>Monitores</CardTitle>
            <CardDescription>{data.funcoes.monitor.length} responsáveis</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-sm">
                  <th className="text-left p-2">Nick</th>
                  <th className="text-center p-2">Tempo</th>
                </tr>
              </thead>
              <tbody>
                {[...data.funcoes.monitor]
                  .sort((a, b) => b.tempoTotal - a.tempoTotal)
                  .map((resp) => (
                  <tr key={resp.militar.id} className="border-b hover:bg-secondary/30">
                    <td className="p-2">
                      <div className="font-medium text-sm">{resp.militar.nome}</div>
                      <div className="text-xs text-muted-foreground">{resp.militar.patente}</div>
                    </td>
                    <td className="p-2 text-center text-sm">{formatMinutos(resp.tempoTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Auxiliares */}
        <Card>
          <CardHeader>
            <CardTitle>Auxiliares</CardTitle>
            <CardDescription>{data.funcoes.auxiliar.length} responsáveis</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-sm">
                  <th className="text-left p-2">Nick</th>
                  <th className="text-center p-2">Tempo</th>
                </tr>
              </thead>
              <tbody>
                {[...data.funcoes.auxiliar]
                  .sort((a, b) => b.tempoTotal - a.tempoTotal)
                  .map((resp) => (
                  <tr key={resp.militar.id} className="border-b hover:bg-secondary/30">
                    <td className="p-2">
                      <div className="font-medium text-sm">{resp.militar.nome}</div>
                      <div className="text-xs text-muted-foreground">{resp.militar.patente}</div>
                    </td>
                    <td className="p-2 text-center text-sm">{formatMinutos(resp.tempoTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Consolidada de Patrulhas por Oficial */}
      <Card>
        <CardHeader>
          <CardTitle>Patrulhas por Oficial</CardTitle>
          <CardDescription>Consolidação de todas as patrulhas marcadas pelos oficiais</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-sm">
                <th className="text-left p-2">Nick</th>
                <th className="text-center p-2">Patrulhas Concluídas</th>
                <th className="text-center p-2">Patrulhas Incompletas</th>
                <th className="text-center p-2">Patrulhas Canceladas</th>
                <th className="text-center p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Consolidar dados por militar (mesmo militar pode ter múltiplas funções)
                const consolidadoMap = new Map<string, {
                  militar: { id: string; nome: string; patente: string };
                  concluidas: number;
                  incompletas: number;
                  canceladas: number;
                }>();

                data.responsaveis.forEach((resp) => {
                  if (consolidadoMap.has(resp.militar.id)) {
                    const existing = consolidadoMap.get(resp.militar.id)!;
                    existing.concluidas += resp.patrulhasConcluidas;
                    existing.incompletas += resp.patrulhasIncompletas;
                    existing.canceladas += resp.patrulhasCanceladas;
                  } else {
                    consolidadoMap.set(resp.militar.id, {
                      militar: resp.militar,
                      concluidas: resp.patrulhasConcluidas,
                      incompletas: resp.patrulhasIncompletas,
                      canceladas: resp.patrulhasCanceladas,
                    });
                  }
                });

                const consolidados = Array.from(consolidadoMap.values())
                  .filter(c => (c.concluidas + c.incompletas + c.canceladas) > 0)
                  .sort((a, b) => {
                    const totalA = a.concluidas + a.incompletas + a.canceladas;
                    const totalB = b.concluidas + b.incompletas + b.canceladas;
                    return totalB - totalA;
                  });

                return consolidados.map((item) => {
                  const total = item.concluidas + item.incompletas + item.canceladas;
                  return (
                    <tr key={item.militar.id} className="border-b hover:bg-secondary/30">
                      <td className="p-2">
                        <div className="font-medium">{item.militar.nome}</div>
                        <div className="text-xs text-muted-foreground">{item.militar.patente}</div>
                      </td>
                      <td className="p-2 text-center text-green-600 font-medium">{item.concluidas}</td>
                      <td className="p-2 text-center text-yellow-600 font-medium">{item.incompletas}</td>
                      <td className="p-2 text-center text-red-600 font-medium">{item.canceladas}</td>
                      <td className="p-2 text-center font-bold">{total}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Tabela de Patrulheiros */}
      <Card>
        <CardHeader>
          <CardTitle>Patrulhas por Patrulheiro</CardTitle>
          <CardDescription>Estatísticas de patrulhas realizadas pelos militares</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-sm">
                <th className="text-left p-2">Nick</th>
                <th className="text-center p-2">Patrulhas Concluídas</th>
                <th className="text-center p-2">Patrulhas Incompletas</th>
                <th className="text-center p-2">Patrulhas Canceladas</th>
                <th className="text-center p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.patrulheiros
                .filter(p => (p.patrulhasConcluidas + p.patrulhasIncompletas + p.patrulhasCanceladas) > 0)
                .sort((a, b) => {
                  const totalA = a.patrulhasConcluidas + a.patrulhasIncompletas + a.patrulhasCanceladas;
                  const totalB = b.patrulhasConcluidas + b.patrulhasIncompletas + b.patrulhasCanceladas;
                  return totalB - totalA;
                })
                .map((patrulheiro) => {
                  const total = patrulheiro.patrulhasConcluidas + patrulheiro.patrulhasIncompletas + patrulheiro.patrulhasCanceladas;
                  return (
                    <tr key={patrulheiro.militar.id} className="border-b hover:bg-secondary/30">
                      <td className="p-2">
                        <div className="font-medium">{patrulheiro.militar.nome}</div>
                        <div className="text-xs text-muted-foreground">{patrulheiro.militar.patente}</div>
                      </td>
                      <td className="p-2 text-center text-green-600 font-medium">{patrulheiro.patrulhasConcluidas}</td>
                      <td className="p-2 text-center text-yellow-600 font-medium">{patrulheiro.patrulhasIncompletas}</td>
                      <td className="p-2 text-center text-red-600 font-medium">{patrulheiro.patrulhasCanceladas}</td>
                      <td className="p-2 text-center font-bold">{total}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
